import React, {useEffect, useState} from 'react';
import {ActivityIndicator, Platform, ScrollView, View} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {useDispatch, useSelector} from 'react-redux';
import {
  RouteProp,
  useIsFocused,
  useNavigation,
  useRoute,
} from '@react-navigation/native';

import FilterButton from '/component/base/FilterButton';
import Icon from '/component/base/Icon';
import Text from '/component/base/Text';
import TouchableOpacity from '/component/base/TouchableOpacity';
import ModalHeader from '/component/layout/ModalHeader/ModalHeader';
import MyTeamsListHeader from '/component/partial/report/MyTeamsListHeader';
import PeopleReportDataRenderer from '/component/partial/report/PeopleReportDataRenderer';
import ReportDataProvider from '/component/partial/report/ReportDataProvider';
import {FEATURE_FLAGS_CONSTANT} from '/constant/featureFlag.constants';
import {Reports} from '/constant/TestIDs';
import MyTeams from '/constant/TestIDs/MyTeams';
import {MY_TEAMS} from '/enum/records.enum';
import {REPORT_STATE_IS_LOADING} from '/enum/reportState.enum';
import {useTranslation} from '/provider/TranslationProvider';
import {setIsViewTeam, updateMyTeamsFilter} from '/redux/action/myTeams.action';
import {viewTeamsLoadingState} from '/redux/selector/myTeams.selector';
import {allReportState} from '/redux/selector/report.selector';
import {watchlistCount} from '/redux/selector/watchList.selector';
import {denim} from '/theme/color/secondary.color';
import {
  getDefaultNewCoachesKey,
  getDefaultNewCoachesValue,
} from '/util/filter.util';
import testProperties from '/util/testing/testProperties';
import {useFeatureFlag} from '/util/useFeatureFlag.util';

import {useClearMyTeamsFilters} from './hooks/useClearMyTeamsFilters';
import {useMyTeamsAnalytics} from './hooks/useMyTeamsAnalytics';
import {useMyTeamsContext} from './hooks/useMyTeamsContext';
import {useMyTeamsEffectiveParams} from './hooks/useMyTeamsEffectiveParams';
import {useMyTeamsFiltersController} from './hooks/useMyTeamsFiltersController';
import {useMyTeamsHeaderTitle} from './hooks/useMyTeamsHeaderTitle';
import {useMyTeamsNavigation} from './hooks/useMyTeamsNavigation';
import {useMyTeamsNavigationState} from './hooks/useMyTeamsNavigationState';
import {useMyTeamsReportParams} from './hooks/useMyTeamsReportParams';
import {useMyTeamsRouteParams} from './hooks/useMyTeamsRouteParams';
import {useMyTeamsScreenContext} from './hooks/useMyTeamsScreenContext';
import {useMyTeamsTabs} from './hooks/useMyTeamsTabs';
import ModalFilter from './ModalPopoverFilter/ModalPopoverFilter.connect';
import {getDefaultFilters} from './util/myTeamsFilterDomain.util';
import {ClearFilter} from './ClearFilter';
import {getBottomInsetStyle, styles} from './MyTeamsContent.style';
import {MyTeamsContentProps} from './MyTeamsContent.types';
import {generateOnRecordPress} from './MyTeamsReport.util';
import reportPayload from './reportPayload';
import TeamOrgReportList from './TeamOrgReportList';

import {useRecordsSorting} from '/context/RecordsSort/useRecordsSorting';

export const MyTeamsContent = (data: MyTeamsContentProps) => {
  const dispatch = useDispatch();
  const insets = useSafeAreaInsets();

  const ffTeamsOrgSR = useFeatureFlag(FEATURE_FLAGS_CONSTANT.TEAMS_ORG_SR);
  const defaultNewCoachesValue = getDefaultNewCoachesValue(ffTeamsOrgSR);
  const defaultNewCoachesKey = getDefaultNewCoachesKey(ffTeamsOrgSR);

  const {
    recordsShowRecord,
    showRecordPopover,
    recordsShowPopover,
    resetMyTeamsData,
    count,
    loadingData,
    resetViewTeamsData,
  } = data;

  const navigation = useNavigation();
  const route = useRoute<RouteProp<any, any>>();
  const routeParams = useMyTeamsRouteParams(route, data);

  const reduxWatchlistCount = useSelector(watchlistCount);
  const reportIsLoading = useSelector((state: any) => {
    const allState = allReportState(state) ?? {};
    const isReportLoading = Object.entries(allState).some(
      ([key, entry]: [string, any]) =>
        key.startsWith(MY_TEAMS) && entry?.[REPORT_STATE_IS_LOADING] === true,
    );
    return isReportLoading || viewTeamsLoadingState(state);
  });
  const {reduxActiveTab, reduxIsViewTeam} = useMyTeamsContext();

  const {isViewTeam, onPress, title: recoveredTitle} = routeParams;

  const {tt, ts} = useTranslation('report.myTeams');
  const defaultPageTitle = useMyTeamsHeaderTitle(route, reduxActiveTab, tt);
  const pageTitle = recoveredTitle || defaultPageTitle;
  const [loading, setLoading] = useState(true);

  const {isReadyToLoad, sortParam, isViewTeamsSortParams, resetSortParams} =
    useRecordsSorting({
      recordsName: reportPayload.reportName,
      isViewTeam: reduxIsViewTeam,
    });

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const {reportHeading = ''} = data || {};
  const defaultFrontlineOnly = reportHeading === 'New Frontline Coaches';
  // Combined back handler for both Android and iOS
  const isFocused = useIsFocused();

  const {
    effectiveLastPaidRank,
    effectiveHighestAchievedRank,
    effectiveCoachCurrentRank,
    effectiveUserId,
  } = useMyTeamsEffectiveParams(data, routeParams);

  const {
    currentFilters,
    updateCurrentTabFilters,
    handleResetFilters,
    setActiveTab,
    filterCount,
    toggleFrontlineFilter,
    modalVisible,
    setModalVisible,
    clickedFilterType,
    applyFilter,
    setApplyFilter,
    openModal,
  } = useMyTeamsFiltersController({
    reportHeading,
    ffTeamsOrgSR,
    initialTab: 'my-teams',

    defaultNewCoachesKey,
    defaultNewCoachesValue,

    effectiveLastPaidRank,
    effectiveHighestAchievedRank,
    effectiveCoachCurrentRank,
    effectiveUserId,
  });

  const {
    newCoachesOptions = [],
    frontlineOnly = false,
    newCoachesWithoutFilter = undefined,
  } = currentFilters ?? {};

  const NewCoachesOptionsFilterValue = newCoachesOptions?.find(
    NewCoachesItem => NewCoachesItem.selected === true,
  );

  const {buildFiltersData, buildReportParams} = useMyTeamsReportParams({
    sortParam: sortParam,
    defaultNewCoachesKey,
    defaultNewCoachesValue,
    ffTeamsOrgSR,
    newCoachesWithoutFilter,
    NewCoachesOptionsFilterValue,
  });

  const {routes, coachID, coachIDparams} = useMyTeamsNavigationState();
  const effectiveCoachIDparams = routeParams.coachId || coachIDparams;

  const {handleBackNavigation} = useMyTeamsNavigation({
    routes,
    coachID,
    coachIDparams: effectiveCoachIDparams,
    defaultNewCoachesKey,
    defaultFrontlineOnly,
    handleResetFilters,
  });
  const clearMyTeamsFilters = useClearMyTeamsFilters();

  const isNewFrontlineReport = reportHeading === 'New Frontline Coaches';
  useEffect(() => {
    if (isNewFrontlineReport) {
      dispatch(
        updateMyTeamsFilter({
          ...getDefaultFilters(defaultNewCoachesKey),
          frontlineOnly: true,
        }),
      );
    }
  }, [isNewFrontlineReport]);

  // Reset when returning from view teams
  useEffect(() => {
    const unsubscribe = navigation.addListener('focus', () => {
      if (isReadyToLoad) {
        resetSortParams();

        handleResetFilters();

        // Clear Redux filters
        clearMyTeamsFilters(defaultNewCoachesKey);
      }
    });

    return unsubscribe;
  }, [navigation, isReadyToLoad, resetSortParams, isFocused]);

  // Reset filters and sorting when navigating to/from view teams
  useEffect(() => {
    const resetFlag = (route?.params as any)?.resetFiltersAndSorting;

    if (resetFlag && isReadyToLoad) {
      resetSortParams();
      // Clear all filters in Redux (same as ClearFilter button)
      clearMyTeamsFilters(defaultNewCoachesKey);
      handleResetFilters(undefined, defaultFrontlineOnly, false);
    }
  }, [(route?.params as any)?.resetFiltersAndSorting, isReadyToLoad]);

  useMyTeamsScreenContext(reduxIsViewTeam, reduxActiveTab);

  useEffect(() => {
    if (isViewTeam !== undefined) {
      dispatch(setIsViewTeam(isViewTeam));
    }
  }, [isViewTeam, dispatch]);

  const {handleTabPress} = useMyTeamsTabs({
    navigation,
    setActiveTab,
    coachIDparams: effectiveCoachIDparams,
    defaultFrontlineOnly,
    defaultNewCoachesKey,
    resetSortParams,
    handleResetFilters,
    resetMyTeamsData,
    resetViewTeamsData,
    effectiveLastPaidRank,
    effectiveHighestAchievedRank,
    effectiveCoachCurrentRank,
    effectiveUserId,
  });

  // Use Redux selector directly if count prop is not available
  const finalCount =
    typeof count === 'number' && count >= 0
      ? count
      : typeof reduxWatchlistCount === 'number' && reduxWatchlistCount >= 0
      ? reduxWatchlistCount
      : 0;

  const {trackTeamSearch} = useMyTeamsAnalytics({
    effectiveLastPaidRank,
    effectiveHighestAchievedRank,
    effectiveCoachCurrentRank,
    effectiveUserId,
  });

  const hasActiveFilters = filterCount > 0;

  // Prevent a blank screen while sort params/context is still initializing.
  if (!isReadyToLoad || loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={denim} />
      </View>
    );
  }
  const filtersDataMemo = buildFiltersData();
  const reportParamsMemo = buildReportParams();

  const isWeb = Platform.OS === 'web';
  const ConditionalWrapper = ({
    condition,
    wrapper,
    children,
  }: {
    condition: boolean;
    wrapper: (children: React.ReactNode) => JSX.Element;
    children: React.ReactNode;
  }) => (condition ? wrapper(children) : <>{children}</>);

  const content = (
    <>
      <ModalHeader
        titleVariant="heading"
        isProfileScreen
        titlePressEnabled={Platform.OS !== 'web'}
        onClose={handleBackNavigation}
        closeIconName={'chevron-left'}
        title={pageTitle}
        actionLabel={<Icon name="search" style={styles.SearchIcon} />}
        onActionPress={() => {
          trackTeamSearch();
          navigation.navigate('search' as never);
        }}
        style={{
          base:
            Platform.OS === 'web'
              ? {
                  ...styles.base,
                  ...((styles as Record<string, unknown>)
                    .modalHeaderBase as object),
                }
              : styles.base,
          headerText: styles.headerText,
        }}>
        {/* Tabs */}
        <View style={styles.tabBar}>
          <TouchableOpacity
            testID={MyTeams.myTeamsMyTeamsToggleButton}
            disabled={reportIsLoading}
            style={[
              styles.tabItem,
              reduxActiveTab === 'my-teams' && styles.activeTab,
              reportIsLoading && styles.disabled,
            ]}
            onPress={() => handleTabPress('my-teams')}>
            <Text style={styles.tabText} isBold>
              My Teams
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            testID={MyTeams.myTeamsWatchlistToggleButton}
            disabled={reportIsLoading}
            style={[
              styles.tabItem,
              reduxActiveTab === 'watchlist' && styles.activeTab,
              reportIsLoading && styles.disabled,
            ]}
            onPress={() => handleTabPress('watchlist')}>
            <Text style={styles.tabText} isBold>
              Watchlist ({finalCount})
            </Text>
          </TouchableOpacity>
        </View>
      </ModalHeader>

      {/* Filters Section */}
      <View
        {...testProperties(null, true)}
        style={isWeb ? styles.reportWrapper : undefined}>
        <ScrollView
          overScrollMode="never"
          bounces={false}
          showsHorizontalScrollIndicator={false}
          horizontal={!ffTeamsOrgSR}
          contentContainerStyle={styles.scrollViewContainer}
          scrollEnabled
          {...testProperties(Reports.reportFiltersScrollView, true)}>
          <View style={styles.filtersButtonContainer}>
            <View style={styles.viewContainer} {...testProperties(null, true)}>
              <ConditionalWrapper
                condition={isWeb}
                wrapper={children => (
                  <View style={styles.buttonFlex}>{children}</View>
                )}>
                <FilterButton
                  testID={MyTeams.myTeamsFilterFrontlineOnlyButton}
                  onPress={toggleFrontlineFilter}
                  hideArrow={true}
                  text={tt('frontline', 'Frontline Only')}
                  style={styles.buttonWidth}
                  active={frontlineOnly}
                  disabled={reportIsLoading}
                />
              </ConditionalWrapper>

              {ffTeamsOrgSR && (
                <ConditionalWrapper
                  condition={isWeb}
                  wrapper={children => (
                    <View style={styles.buttonFlex}>{children}</View>
                  )}>
                  <TouchableOpacity
                    {...testProperties(Reports.reportFilterButton, true)}
                    accessible={true}
                    accessibilityLabel="Filters"
                    accessibilityRole="button"
                    disabled={reportIsLoading}
                    style={[
                      styles.buttonFilter,
                      hasActiveFilters && styles.buttonFilterActive,
                      reportIsLoading && styles.disabled,
                    ]}
                    onPress={() => openModal('all')}>
                    <Icon
                      name="filter"
                      style={[
                        styles.iconReport,
                        hasActiveFilters && styles.iconActive,
                      ]}
                    />

                    <View style={styles.filterTextWrapper}>
                      <Text
                        style={[
                          styles.text,
                          hasActiveFilters && styles.textActive,
                        ]}
                        testID={Reports.reportFilterButtonLabel}>
                        {tt('filter', 'Filters')}
                      </Text>

                      {hasActiveFilters && <View style={styles.filterDot} />}
                    </View>
                  </TouchableOpacity>
                </ConditionalWrapper>
              )}
            </View>

            {ffTeamsOrgSR && filterCount > 0 && (
              <View style={styles.clearFilterRow}>
                <ClearFilter
                  testID={MyTeams.myTeamsClearButton}
                  {...reportPayload}
                  text={ts('clearFilter', 'Clear Filters')}
                  filters={currentFilters}
                  updateFilters={updateCurrentTabFilters}
                  disabled={reportIsLoading}
                />
              </View>
            )}
          </View>
        </ScrollView>

        <ModalFilter
          {...reportPayload}
          setModalVisible={setModalVisible}
          modalVisible={modalVisible}
          reportHeading={reportHeading}
          applyFilter={applyFilter}
          setApplyFilter={setApplyFilter}
          filters={currentFilters}
          updateFilters={updateCurrentTabFilters}
          filterCount={filterCount}
          reportName={MY_TEAMS}
          loadingData={loadingData}
          reportParams={[
            {
              ...buildReportParams(),
              ...(isViewTeamsSortParams ? {isViewTeamsSortParams: true} : {}),
            },
          ]}
          clickedFilterType={clickedFilterType}
          isViewTeam={isViewTeam}
        />
      </View>

      {/* Report List */}
      <View style={isWeb ? styles.reportWrapperList : styles.contentContainer}>
        <ReportDataProvider
          {...reportPayload}
          filtersData={filtersDataMemo}
          dataRenderer={PeopleReportDataRenderer}
          dataRendererProps={{
            listHeaderComponent: MyTeamsListHeader,
            teamOrgReportListComponent: TeamOrgReportList,
            onRecordPress: generateOnRecordPress({
              showRecordPopover,
              recordsShowPopover,
              recordsShowRecord,
            }),
            filtersData: filtersDataMemo,
            coachId: effectiveCoachIDparams,
          }}
          isFocused={isFocused}
          loadMore={loadingData}
          loadingData={data}
          onPress={onPress}
          reportParams={[
            {
              ...reportParamsMemo,
              ...(isViewTeamsSortParams ? {isViewTeamsSortParams: true} : {}),
            },
          ]}
        />
      </View>

      {/* Android Bottom Safe Area */}
      {Platform.OS === 'android' && insets.bottom > 0 ? (
        <View style={[styles.bottomView, getBottomInsetStyle(insets.bottom)]} />
      ) : null}
    </>
  );

  return content;
};
